import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import { usePermissionStore } from '@/lib/permissions/store';
import { useCurrentUserModules } from '@/lib/permissions/use-current-modules';
import { useThemeStore } from '@/lib/theme/store';
import { filterNav } from './filter-nav';
import { NAV_ITEMS, isGroup, type NavGroup, type NavLeaf } from './nav-config';
import { isRouteActive } from './route-active';

/**
 * Active-pill styling adapts to the sidebar palette:
 *  - dark sidebars (bold/brand): light pill via color-inverted background
 *    PLUS iOS-style inverse-rounded notches above/below so the sidebar
 *    appears to curl around the active item (design 1 reference).
 *  - light sidebars (subtle): soft brand tint with brand text — high
 *    contrast against the light bg without an aggressive dark pill that
 *    fights with the rest of the sidebar. No notches in subtle mode.
 */
function useActivePillClasses() {
  const style = useThemeStore((s) => s.sidebarStyle);
  if (style === 'subtle') {
    return {
      leaf: 'rounded-l-md rounded-r-2xl bg-primary/10 font-semibold text-primary ring-1 ring-primary/20',
      sub: 'rounded-l-md rounded-r-xl bg-primary/10 font-medium text-primary ring-1 ring-primary/20',
      activeIcon: 'text-primary',
    } as const;
  }
  // Clean rounded-right pill that sits within the sidebar bounds — no
  // protrusion past the edge, no notches. The contrast against the
  // darker sidebar bg is enough on its own.
  return {
    leaf: 'rounded-l-md rounded-r-2xl bg-sidebar-foreground text-sidebar shadow-sm ring-1 ring-sidebar-border',
    sub: 'rounded-l-md rounded-r-xl bg-sidebar-foreground font-medium text-sidebar shadow-sm ring-1 ring-sidebar-border',
    activeIcon: 'text-sidebar',
  } as const;
}

// (Carve-out notches removed. The active pill on its own — inverted
// sidebar colors, rounded right corners, soft shadow — is the
// "previous design" the user wants back.)

interface SidebarContentProps {
  /** Hide labels (compact icon-only mode used by collapsed left/right sidebar). */
  compact?: boolean;
}

/**
 * Vertical sidebar nav content. Top-placement renders nav via TopBar in
 * Sidebar.tsx — this component is vertical-only.
 */
export function SidebarContent({ compact = false }: SidebarContentProps) {
  const role = useAuthStore((s) => s.role);
  const isSubSuperAdmin = useAuthStore((s) => s.isSubSuperAdmin());
  const perms = usePermissionStore((s) => s.perms);
  const { modules } = useCurrentUserModules();
  const location = useLocation();

  const items = useMemo(
    () => filterNav(NAV_ITEMS, { role, isSubSuperAdmin, perms, modules }),
    [role, isSubSuperAdmin, perms, modules],
  );

  /*
   * Accordion controller — only ONE group is expanded at a time. When the
   * user clicks a different group's header (or navigates into another
   * group's child), the previously expanded group collapses. The label
   * string is used as the identity key because labels are unique in the
   * nav tree.
   */
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  // On every navigation, re-sync openGroup to whichever group contains
  // the active route. If the route isn't inside any group, the
  // accordion closes entirely. Clicking a sibling header (without
  // navigating) is handled via setOpenGroup in VerticalGroup itself.
  useEffect(() => {
    for (const item of items) {
      if (
        isGroup(item) &&
        item.children.some((c) => isRouteActive(location.pathname, c.to))
      ) {
        setOpenGroup(item.label);
        return;
      }
    }
    setOpenGroup(null);
  }, [items, location.pathname]);

  // pr-0 on the nav wrapper lets the active pill extend flush to the
  // sidebar's right edge — the carve-out illusion from design 1.
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item, idx) =>
        isGroup(item) ? (
          <VerticalGroup
            key={`${item.label}-${idx}`}
            group={item}
            compact={compact}
            pathname={location.pathname}
            open={openGroup === item.label}
            onToggle={(next) => setOpenGroup(next ? item.label : null)}
          />
        ) : (
          <VerticalLeaf key={`${item.label}-${idx}`} leaf={item} compact={compact} />
        ),
      )}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/* Vertical leaf — top-level item without children.                    */
/* ------------------------------------------------------------------ */

function VerticalLeaf({ leaf, compact }: { leaf: NavLeaf; compact: boolean }) {
  const Icon = leaf.icon;
  const pill = useActivePillClasses();
  // Carve-out active state: the active row swaps its background to the
  // page color with deeply-rounded right corners + a slim rounded left
  // edge, so the dark sidebar appears to scoop around a light pill that
  // visually attaches to the page. No pseudo-element notches — the
  // contrast + rounded corners are enough at this scale.
  return (
    <li>
      <NavLink
        to={leaf.to}
        end={leaf.to === '/'}
        className={({ isActive }) =>
          cn(
            'group/leaf relative flex items-center gap-2.5 py-2 pl-3 pr-3 text-sm font-medium transition-colors duration-150',
            !isActive &&
              'rounded-md text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
            // Style-aware active pill — see useActivePillClasses().
            isActive && pill.leaf,
            compact && 'justify-center px-0',
          )
        }
        title={compact ? leaf.label : undefined}
      >
        {({ isActive }) => (
          <>
            {Icon && (
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                  isActive
                    ? pill.activeIcon
                    : 'text-sidebar-foreground/70 group-hover/leaf:text-sidebar-foreground',
                )}
              >
                <Icon className="h-[16px] w-[16px]" />
              </span>
            )}
            {!compact && <span className="flex-1 truncate">{leaf.label}</span>}
          </>
        )}
      </NavLink>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Vertical group — has children; expands inline.                      */
/* ------------------------------------------------------------------ */

function VerticalGroup({
  group,
  compact,
  pathname,
  open,
  onToggle,
}: {
  group: NavGroup;
  compact: boolean;
  pathname: string;
  /** Controlled by the SidebarContent accordion. */
  open: boolean;
  onToggle: (next: boolean) => void;
}) {
  const containsActive = useMemo(
    () => group.children.some((c) => isRouteActive(pathname, c.to)),
    [group.children, pathname],
  );
  const Icon = group.icon;

  if (compact) {
    return <CompactGroupPopover group={group} pathname={pathname} />;
  }

  return (
    <li className={cn('rounded-md transition-colors', open && 'bg-sidebar-accent/25')}>
      <button
        type="button"
        onClick={() => onToggle(!open)}
        className={cn(
          'group/group relative flex w-full items-center gap-2.5 rounded-md py-1.5 pl-2 pr-2 text-sm font-medium transition-colors',
          'text-sidebar-foreground/80 hover:text-sidebar-foreground',
          !open && 'hover:bg-sidebar-accent/40',
          containsActive && !open && 'text-sidebar-foreground',
        )}
        aria-expanded={open}
      >
        <span
          aria-hidden
          className={cn(
            'absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full bg-primary transition-all duration-150',
            containsActive && !open ? 'h-4 w-[3px]' : 'h-0 w-[3px]',
          )}
        />
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
            open || containsActive
              ? 'bg-primary/15'
              : 'text-sidebar-foreground/70 group-hover/group:text-sidebar-foreground',
          )}
        >
          <Icon className="h-[16px] w-[16px]" />
        </span>
        <span className="flex-1 truncate text-left">{group.label}</span>
        {!open && containsActive && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
        )}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50 transition-transform duration-200',
            open && 'rotate-180 text-sidebar-foreground/80',
          )}
        />
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          {/*
           * Tree-view sub-list: a single continuous vertical guide line
           * runs down the left edge, and each sub-item draws its own
           * little `╰` elbow that picks up from the line and curves out
           * to the row. Same shape used by the Brainwave reference.
           */}
          <ul className="relative ml-[18px] mr-1 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-muted/60 pb-1 pl-4">
            {group.children.map((child) => (
              <SubItem key={child.to} child={child} pathname={pathname} />
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
}

function SubItem({ child, pathname }: { child: NavLeaf; pathname: string }) {
  const active = isRouteActive(pathname, child.to);
  const pill = useActivePillClasses();
  return (
    <li className="relative">
      {/*
       * Curved connector — a small `╰` elbow that picks up from the
       * parent ul's vertical guide line and curves out toward the sub-
       * item's left edge. Drawn as a positioned <span> with border-left,
       * border-bottom, and a bottom-left radius so the corner reads as a
       * soft curve instead of a sharp turn.
       */}
      <span
        aria-hidden
        className="pointer-events-none absolute -left-[17px] top-0 h-[18px] w-[14px] border-b border-l border-sidebar-muted/60"
        style={{ borderBottomLeftRadius: 10 }}
      />
      <NavLink
        to={child.to}
        className={cn(
          'group/sub relative flex items-center gap-2.5 py-1.5 pl-2 pr-2 text-[13px] transition-colors',
          active
            ? pill.sub
            : 'rounded-md text-sidebar-foreground/65 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
        )}
      >
        <span className="truncate">{child.label}</span>
      </NavLink>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Compact (icon-only) group — opens a popout panel.                   */
/* ------------------------------------------------------------------ */

function CompactGroupPopover({ group, pathname }: { group: NavGroup; pathname: string }) {
  const [open, setOpen] = useState(false);
  const containsActive = group.children.some((c) => isRouteActive(pathname, c.to));
  const Icon = group.icon;

  return (
    <li className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group/cgroup relative flex h-9 w-full items-center justify-center rounded-md text-sm transition-colors',
          'text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
          containsActive && 'text-sidebar-foreground',
        )}
        title={group.label}
      >
        <span
          aria-hidden
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
            containsActive && 'bg-primary/15',
          )}
        >
          <Icon className="h-[16px] w-[16px]" />
        </span>
        {containsActive && (
          <span
            aria-hidden
            className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary"
          />
        )}
      </button>
      {open && (
        <ul className="absolute left-full top-0 z-30 ml-1 min-w-44 rounded-md border border-sidebar-border bg-sidebar p-1 shadow-popover">
          {group.children.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                    'text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
                    isActive && 'bg-primary/10 font-medium text-sidebar-foreground',
                  )
                }
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sidebar-muted/40" />
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
